// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Material UI Components
import { Grid, Paper } from "@mui/material";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import { Alert, AlertTitle } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import ImageEntraID from "../imgs/iconEntraID.svg";
import AuditGrowth from "../components/reports/AuditGrowth";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import FilterPanel from "../components/common/FilterPanel";
import PeriodDropDown from "../components/reports/PeriodDropDown";
import EntraIDFailedLogins from "../components/reports/EntraIDFailedLogins";
import EntraIDSuccessfulLogins from "../components/reports/EntraIDSuccessfulLogins";
import EntraIDGroupAndUserActivity from "../components/reports/EntraIDGroupAndUserActivity";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { NumberBadge } from "../utilities/common-ui-utils";
import {
    REPORT_PERIOD_LIST,
    REPORT_ON_PERIOD_30DAYS,
    REPORT_ON_PERIOD_90DAYS,
    REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE,
} from "../constants/constants-reportperiods";
import { formatAuditJobRunTimeDateServerTimeToGMT } from "../utilities/common-date-utils";
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../constants/constants-roles";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

const theBreadcrumbPath = [
    { name: "Home", link: "/" },
    { name: "Reports", link: "/Reports/ReportsHome" },
    { name: "Entra ID Reports", link: "/Reports/EntraID" },
];

const theSectionTitle = "Entra ID Reports Dashboard";
const theSectionDesc =
    "Audit logs in Entra ID provide access to system activity records, often needed for compliance. Sign-in activity, user and group management, directory administration, application registrations, and security + risk events are reflected.  The logs are categorized by user, group, and application management.  Below is a visual representation of your essential Entra ID audit data.";
const isAdminPage = false;

// Common styles for report links inside tab panels
const linkListSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
};

const reportLinkSx = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'normal',
    marginBottom: 0.5,
};


/*
Displays the Entra ID Reports home page for Company X.
*/
function ReportsEntraID(props) {
    const { user, openCompanyNag, setOpenCompanyNag } = props;

    // Component state
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [focusTenantId, setFocusTenantId] = useState("");
    const [focusPeriodId, setFocusPeriodId] = useState(REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE);
    const [accessDenied, setAccessDenied] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();

    const [totalEntraAuditRecords, setTotalEntraAuditRecords] = useState(null);
    const [loadingTotalEntraAuditRecords, setLoadingTotalEntraAuditRecords] = useState(false);
    const [totalEntraAuditRecordsLoaded, setTotalEntraAuditRecordsLoaded] = useState(false);

    const [selectedTenantOption, setSelectedTenantOption] = useState("");
    const [selectedPeriodOption, setSelectedPeriodOption] = useState("");
    const [tabValue, setTabValue] = useState(0);

    // Load TenantHealthStats given the tenantId.
    async function fetchTenantHealthStats(theTenantId) {
        setLoading(true);
        try {
            const response2 = await getHealthStatsByTenantId(theTenantId, 4, user);
            if (response2) {
                setTenantHealthStats(response2);
            }
        } catch (e) {
            console.log("ERROR: ReportsEntraID.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        } finally {
            setLoading(false);
        }
    }

    // Load the total Entra audit records on demand.
        async function fetchTotalAuditRecordsOnDemand() {
            if (!user?.tenantId || loadingTotalEntraAuditRecords || totalEntraAuditRecordsLoaded) {
                return;
            }
    
            setLoadingTotalEntraAuditRecords(true);
    
            try {
                // Heavier on-demand total counts load.
                const response = await getHealthStatsByTenantId(user.tenantId, 14, user);            
                if (response) {
                    const entraTotal = response.totalEntraIDAuditRecords;
                    setTotalEntraAuditRecords(entraTotal);
                    setTotalEntraAuditRecordsLoaded(true);                
                }
            }
            catch (e) {
                console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
                console.log(e);
                setServiceIsDownError(true);
            }
            finally {
                setLoadingTotalEntraAuditRecords(false);
            }
        }

    useEffect(() => {
        async function fetchCompanyData() {
            try {
                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }
                if (user && user.companyId) {
                    const response1 = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
                    if (response1) {
                        setCompany(response1);

                        if (response1.tenantList && response1.tenantList.length > 0) {
                            const defaultTenant = response1.tenantList[0];
                            setSelectedTenantOption(defaultTenant);
                            fetchTenantHealthStats(defaultTenant.id);
                        }

                        setSelectedPeriodOption(
                            REPORT_PERIOD_LIST[REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE]
                        );
                    }
                }
            } catch (e) {
                console.log("ERROR: ReportsEntraID.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            } finally {
                setLoading(false);
            }
        }

        fetchCompanyData();
    }, []);

    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3, textAlign: "left" }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    }


    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            "aria-controls": `simple-tabpanel-${index}`,
        };
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Event handlers for drop down lists.
    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        fetchTenantHealthStats(event.target.value.id);
    };

    const handlePeriodOptionChange = (event) => {
        setSelectedPeriodOption(event.target.value);
        setFocusPeriodId(event.target.value.id);
    };

    // Component UI
    if (loading) {
        console.log("Loading Data ....");
        return <ProgressBar message="Loading ..." loading={loading} />;
    }

    if (serviceIsDownError) {
        return (
            <>
                <ServiceIsDown />
            </>
        );
    }

    if (!accessDenied && selectedTenantOption && !selectedTenantOption.logEntraIDAudit) {
        return (
            <Alert severity="info">
                <AlertTitle>Entra ID Audit Logging is not enabled for this tenant.</AlertTitle>
                If you would like this feature, please enable Preserve Entra ID Audit Logs by editing your Tenant
                settings (or{" "}
                <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                    Contact your Administrator
                </Link>
                ).
            </Alert>
        );
    }

    // Get the total audit record count.
    let totalAuditRecords = 0;
    if (tenantHealthStats.totalEntraIDAuditRecords) {
        totalAuditRecords = tenantHealthStats.totalEntraIDAuditRecords;
    }

    return (
        <>
            <CustomerPageScaffold
                breadcrumbPaths={theBreadcrumbPath}
                sectionTitle={theSectionTitle}
                sectionDescription={theSectionDesc}
                isAdminPage={isAdminPage}
                company={company}
                selectedTenant={selectedTenantOption}
                openCompanyNag={openCompanyNag}
                setOpenCompanyNag={setOpenCompanyNag}
                headerAdornment={<Box component="img" src={ImageEntraID} alt="Entra ID Reports" sx={{ width: 75, height: 'auto' }} />}
            />


            <Box>
                <Grid container spacing={2}>
                    {accessDenied && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <AccessDenied />
                        </Grid>
                    )}

                    {!selectedTenantOption && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <Alert severity="error">
                                <AlertTitle>
                                    Your Company has not yet completed the setup process or you have not yet specified a
                                    valid Tenant.
                                </AlertTitle>
                            </Alert>
                        </Grid>
                    )}

                    {tenantHealthStats.status === 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <TenantSetupRequired />
                        </Grid>
                    )}

                    {!accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status >= 1 ? (
                        <>
                            <Grid item xs={12}>
                                <FilterPanel title="Dashboard filters">
                                    <PeriodDropDown
                                        selectedPeriodOption={selectedPeriodOption}
                                        handlePeriodOptionChange={handlePeriodOptionChange}
                                    />
                                </FilterPanel>
                            </Grid>

                            {/* Last audit job run time */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                Audit data last updated on:{" "}
                                {formatAuditJobRunTimeDateServerTimeToGMT(
                                    tenantHealthStats.entraIDAuditLastSuccessfulRunTime
                                )}
                                <br />
                            </Grid>

                            {/* Row 1: Growth + Failed Logins (total) + Successful Logins (total) */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <Box sx={{ height: "100%" }}>
                                        <AuditGrowth
                                            tenantId={user.tenantId}
                                            periodToUse={focusPeriodId}
                                            reportType="Entra ID"
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <EntraIDFailedLogins
                                        user={user}
                                        companyId={company.id}
                                        tenantId={user.tenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"operation"}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <EntraIDSuccessfulLogins
                                        user={user}
                                        companyId={company.id}
                                        tenantId={user.tenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"operation"}
                                    />
                                </Paper>
                            </Grid>

                            {/* Row 2: Total Audit Records + Search card */}
                            { /* Display total Entra audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 2 }}
                                        >
                                            Total Audit Records
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            {!totalEntraAuditRecordsLoaded && !loadingTotalEntraAuditRecords && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={fetchTotalAuditRecordsOnDemand}
                                                >
                                                    Show total records
                                                </Button>
                                            )}

                                            {loadingTotalEntraAuditRecords && (
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ fontSize: 15 }}
                                                >
                                                    Loading count...
                                                </Typography>
                                            )}

                                            {totalEntraAuditRecordsLoaded && !loadingTotalEntraAuditRecords && (
                                                <TableContainer sx={{ mt: 1 }}>
                                                    <Table style={{ width: '100%', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell style={{ border: 'none', padding: 2 }}>
                                                                    Entra ID
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{ border: 'none', padding: 2 }}
                                                                >
                                                                    <NumberBadge
                                                                        number={totalEntraAuditRecords?.toLocaleString()}
                                                                        bgcolor="#ffcd56"
                                                                        txtcolor="white"
                                                                    />                          
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        
                            <InsightsSearchBox colSpanVal={8} workloadName={"Entra"} />

                            {/* Row 4: Tabs for Activity/User reports */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%" }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            aria-label="Entra ID Reports Tab"
                                        >
                                            <Tab label="Activity Reports" {...a11yProps(0)} />
                                            <Tab label="User Reports" {...a11yProps(1)} />
                                            <Tab label="Top Activity" {...a11yProps(2)} />
                                        </Tabs>
                                    </Box>
                                    <TabPanel value={tabValue} index={0}>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupMembershipActivityByGroupReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group Membership Activity
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins by User (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins by User (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_30DAYS}/actorIpAddress`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins by IP Address (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDFailedLoginsReport/${REPORT_ON_PERIOD_90DAYS}/actorIpAddress`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins by IP Address (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDSuccessfulLoginsReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Successful Logins (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDSuccessfulLoginsReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Successful Logins (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDSuccessfulLoginsReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Successful Logins by User (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDSuccessfulLoginsReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Successful Logins by User (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupAndUserActivityReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group and User Activity (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupAndUserActivityReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group and User Activity (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupAndUserActivityReport/${REPORT_ON_PERIOD_30DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group and User Activity by Modifying User (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupAndUserActivityReport/${REPORT_ON_PERIOD_90DAYS}/userId`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group and User Activity by Modifying User (Last 3 months)
                                        </Link>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={1}>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDAllActivityByUserReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by User
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDGroupAndUserActivityByUserReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Group and User Activity by Modifying User
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/EntraIDAllActivityByIPAddressReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by IP Address
                                        </Link>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={2}>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/entraid/users')} component="button">Top Users by Activity</Link>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/entraid/operations')} component="button">Top Operations by Activity</Link>
                                    </TabPanel>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        !accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status !== 0 && (
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <ProgressBar message="Loading ..." loading={true} />
                            </Grid>
                        )
                    )}

                    {/* Footer row */}
                    <Grid item xs={12} sx={{ p: 2 }}>
                        <br />
                        Need a custom or new report? Provide us with your{" "}
                        <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                            Product Feedback
                        </Link>
                        .
                        <br />
                        * Unless otherwise specified as UTC, graphs on page are using your browser's local timezone.
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ReportsEntraID;
